import React, { useState, useEffect } from "react";
import "../styles/Contact.css";
import Navbar from "./NavBar";
import Footer from "./Footer";
import Seo from "./Seo";
import { breadcrumbJsonLd } from "../seoSchema";
import { initializeApp } from "firebase/app";
import {
  getFirestore,
  collection,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
} from "firebase/firestore";
import {
  cmsHeroBackgroundStyle,
  resolveFirestoreImage,
  useCmsHeroImageReady,
} from "../firestoreMedia";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
  authDomain: "wishuponaplayhouse.firebaseapp.com",
  projectId: "wishuponaplayhouse",
  storageBucket: "wishuponaplayhouse.firebasestorage.app",
  messagingSenderId: "513377368862",
  appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
  measurementId: "G-NZ116LS0Q1",
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

const EMPTY = { name: "", email: "", phone: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");
  const [heroImage, setHeroImage] = useState(null);
  const [heroReady, setHeroReady] = useState(false);

  useEffect(() => {
    getDoc(doc(firestore, "Pages", "ContactPage"))
      .then((snap) => {
        if (snap.exists() && snap.data().heroImage)
          setHeroImage(snap.data().heroImage);
      })
      .catch(() => {})
      .finally(() => {
        setHeroReady(true);
      });
  }, []);

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      /* 1 ── Save to Firestore (permanent record) */
      await addDoc(collection(firestore, "ContactMessages"), {
        ...form,
        createdAt: serverTimestamp(),
      });

      /* 2 ── Send email via Web3Forms */
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: process.env.REACT_APP_WEB3FORMS_KEY,
          subject: `New Inquiry from ${form.name} — Wish Upon A Playhouse`,
          name: form.name,
          email: form.email,
          phone: form.phone || "Not provided",
          message: form.message,
          from_name: "Wish Upon A Playhouse Website",
        }),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.message || "Web3Forms error");

      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      console.error("Contact submission error:", err);
      setErrorMsg(
        "Something went wrong. Please try emailing us directly at info@wishuponaplayhouse.com",
      );
      setStatus("error");
    }
  };

  const ctHeroUrl = heroReady
    ? resolveFirestoreImage(heroImage, "/images/ranch.png")
    : "";
  const ctHeroImgReady = useCmsHeroImageReady(ctHeroUrl, heroReady);

  return (
    <div className="ct-page">
      <Seo
        title="Contact Us | Wish Upon A Playhouse — Denver, Colorado"
        description="Request a quote for your custom children's playhouse in Denver or Colorado. Call (303) 777-0711 or send a message to start designing your dream backyard playhouse."
        path="/contact"
        jsonLd={breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Contact", path: "/contact" },
        ])}
        jsonLdId="contact-breadcrumb"
      />
      <Navbar />

      {/* ── HERO ── */}
      <div className="sub-hero">
        <div
          className={`sub-hero-bg${heroReady ? "" : " cms-hero-pending-fill"}`}
          style={{
            ...cmsHeroBackgroundStyle(
              !heroReady,
              heroImage,
              "/images/ranch.png",
            ),
            opacity: heroReady ? (ctHeroImgReady ? 1 : 0) : 1,
            transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
        <div className="sub-hero-overlay" />
        <div className="sub-hero-content">
          <p className="sub-hero-eyebrow">We'd love to hear from you</p>
          <h1 className="sub-hero-title">Contact Us</h1>
        </div>
      </div>

      {/* ── MAIN LAYOUT ── */}
      <div className="ct-layout">
        {/* Left: info */}
        <div className="ct-info">
          <p className="ct-info-eyebrow">Get In Touch</p>
          <h2 className="ct-info-heading">Start Your Dream Playhouse</h2>
          <p className="ct-info-sub">
            Have a question or ready to get started? Fill out the form or reach
            us directly — we typically respond within one business day.
          </p>

          <div className="ct-contact-list">
            <a
              href="mailto:info@wishuponaplayhouse.com"
              className="ct-contact-item"
            >
              <div className="ct-contact-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                </svg>
              </div>
              <div>
                <span className="ct-contact-label">Email</span>
                <span className="ct-contact-value">
                  info@wishuponaplayhouse.com
                </span>
              </div>
            </a>
            <a href="tel:3037770711" className="ct-contact-item">
              <div className="ct-contact-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.1 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3 1.22h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.09 9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 21 16z" />
                </svg>
              </div>
              <div>
                <span className="ct-contact-label">Phone</span>
                <span className="ct-contact-value">(303) 777-0711</span>
              </div>
            </a>
            <div className="ct-contact-item">
              <div className="ct-contact-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
                  <circle cx="12" cy="10" r="3" />
                </svg>
              </div>
              <div>
                <span className="ct-contact-label">Location</span>
                <span className="ct-contact-value">Denver, Colorado</span>
              </div>
            </div>
            <a
              href="https://www.facebook.com/profile.php?id=100048386175392"
              target="_blank"
              rel="noreferrer"
              className="ct-contact-item"
            >
              <div className="ct-contact-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                </svg>
              </div>
              <div>
                <span className="ct-contact-label">Facebook</span>
                <span className="ct-contact-value">Wish Upon A Playhouse</span>
              </div>
            </a>
          </div>
        </div>

        {/* Right: form */}
        <div className="ct-form-wrap">
          {status === "success" ? (
            <div className="ct-success">
              <div className="ct-success-icon">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <path d="m9 11 3 3L22 4" />
                </svg>
              </div>
              <h3>Message Sent!</h3>
              <p>
                Thank you for reaching out. We'll be in touch within one
                business day.
              </p>
              <button
                className="ct-reset-btn"
                onClick={() => setStatus("idle")}
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form className="ct-form" onSubmit={handleSubmit}>
              <h3 className="ct-form-title">Send a Message</h3>

              <div className="ct-form-row">
                <div className="ct-field">
                  <label htmlFor="name">Your Name *</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Jane Smith"
                  />
                </div>
                <div className="ct-field">
                  <label htmlFor="phone">Phone Number</label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="(303) 555-0100"
                  />
                </div>
              </div>

              <div className="ct-field">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="jane@example.com"
                />
              </div>

              <div className="ct-field">
                <label htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={6}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us about your project — which style you're interested in, your backyard, child's age, any custom ideas…"
                />
              </div>

              {status === "error" && <p className="ct-error">{errorMsg}</p>}

              <button
                type="submit"
                className="ct-submit"
                disabled={status === "sending"}
              >
                {status === "sending" ? (
                  <>
                    <span className="ct-spinner" /> Sending…
                  </>
                ) : (
                  <>
                    <span>Send Message</span>
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M3 8H13M13 8L9 4M13 8L9 12"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Contact;
